import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Empty, Modal } from 'antd';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { getF2FConsultants, getRandoxClinics } from 'vl-common/src/schemas';
import { AppointmentSlot, RandoxSlot } from 'vl-common/src/types';
import { ArrowLeftOutlined, ArrowRightOutlined } from 'antd/icons';
import { useViewportSize } from 'vl-common/src/hooks/useWindowSize';
import { groupBy, toPairs } from 'lodash';
import type { Clinician as ClinicianType } from 'vl-common/src/schemas/getCliniciansClinicians';
import { useWeekChunkNavigation } from 'vl-common/src/hooks/useWeekChunkNavigation';

export const StyledSlotBox = styled.div`
  margin: 24px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .columns {
    flex-wrap: wrap;
    display: flex;
    row-gap: 6px;
    column-gap: 12px;
    justify-content: space-between;
    align-items: stretch;

    @media (min-width: 768px) {
      column-gap: 8px;
    }
  }
`;

const StyledSlotDay = styled.div<{ $hasSlots: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  flex: 1 1 0%;
  gap: 6px;
  margin-top: 6px;

  .slot-date {
    text-align: center;
    margin: 0;
    margin-bottom: 10px;
    height: 36px;

    @media (min-width: 768px) {
      font-size: 0.95em;
    }

    div {
      white-space: nowrap;
      line-height: 1.2;
    }

    div:first-of-type {
      font-size: 14px;
      font-weight: 400;
      color: #8c8c8c;
    }

    div:last-of-type {
      font-size: 14px;
      font-weight: 300;
      font-weight: ${({ $hasSlots }) => ($hasSlots ? 700 : 400)};
      color: ${({ $hasSlots }) => ($hasSlots ? 'var(--productPrimaryDark)' : '#666')};
    }
  }

  &.placeholder {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 2px;
  }
`;

const StyledSlotButton = styled(Button)`
  display: block;
  padding: 0 1px;
  width: 100%;
  height: 36px;
  border-radius: 4px;
  font-size: 14px;
  border-color: #d9d9d9;
  color: var(--productPrimaryDark) !important;

  &.ant-btn:hover,
  &.ant-btn:focus,
  &.ant-btn:active {
    border-color: var(--productPrimary) !important;
    color: var(--productPrimaryDark) !important;

    > span {
      color: var(--productPrimaryDark) !important;
    }
  }

  &.ant-btn:focus-visible {
    outline: 2px solid var(--productPrimary);
    outline-offset: 2px;
  }

  position: relative;
  overflow: hidden;
  height: 36px;

  span {
    position: relative;
    z-index: 1;
  }

  :not([disabled])::before {
    position: absolute;
    inset: 0;
    background: var(--productPrimary);
    opacity: 0.05;
    display: block;
  }
`;

const StyledMoreButton = styled(Button)`
  font-weight: 500;
  border-radius: 4px;
  padding: 0 7px;
  height: auto;

  &:hover {
    font-weight: 700;
  }

  .more-time {
    margin-left: 3px;

    @media (max-width: 500px) {
      display: none;
    }
  }
`;

const StyledSelectedSlotButton = styled(StyledSlotButton)`
  background: var(--productPrimary);
  color: white;
`;

export const StyledWeekButton = styled(Button)`
  display: flex;
  order: -1;
  flex-basis: calc(50% - 6px);
  align-items: center;
  align-self: start;
  justify-content: center;
  margin-bottom: 6px;
  max-height: 42px;

  @media (min-width: 768px) {
    order: unset;
    flex-basis: unset;
    height: unset;
    justify-content: center;
  }
`;

const StyledEmptyState = styled(Empty)`
  margin: 0;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-empty-image {
    display: none;
  }

  .ant-empty-description {
    font-size: 13px;
    line-height: 1.2;
    color: #666;
  }
`;

// Slice and backfill the slots so that we have 5 slots per day
const sliceAndFill = <T extends {}>(array: T[], length: number) => {
  if (array.length === 0 || !array) return Array.from({ length }).fill(null) as T[];
  if (array.length >= length) return array.slice(0, length);

  return [...array, ...Array.from({ length: length - array.length }).fill(null)] as T[];
};

export type F2FConsultantType = getF2FConsultants.Response['consultantSlots'][0];
export type RandoxSlotType = getRandoxClinics.Response['closestClinics'][0];

const StyledAllSlotsBox = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(100px, 1fr));
  gap: 12px;
  width: 100%;
  max-height: 480px;
  overflow-y: auto;
  overflow-x: auto;

  @media (min-width: 768px) {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  @media (max-width: 468px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const Day = ({
  clinician,
  day,
  slots,
  timezone,
  selectSlot,
  selectedId,
  slotCount = 5,
  slotTooltipUtc
}: {
  clinician: ClinicianType | F2FConsultantType | RandoxSlotType;
  day: string;
  slots: AppointmentSlot[] | RandoxSlot[];
  timezone?: string;
  selectSlot: (slot: AppointmentSlot | RandoxSlot) => void;
  slotCount?: number;
  selectedId?: string;
  slotTooltipUtc?: boolean;
}) => {
  const { t } = useTranslation('common');
  const [showAll, setShowAll] = useState(false);
  const utcDateString = day;
  const dayDate = DateTime.fromISO(utcDateString, { zone: timezone });
  const clinicianFullName =
    ((clinician as ClinicianType).full_name ?? '') ||
    `${(clinician as F2FConsultantType).first_name ?? ''} ${(clinician as F2FConsultantType).last_name ?? ''}` ||
    ((clinician as RandoxSlotType).Name ?? '');

  const selectedSlot = slots?.find((slot) => slot?.slotId && slot.slotId === selectedId);
  const utcSelectedSlotDateTime = selectedSlot ? DateTime.fromJSDate(selectedSlot?.start, { zone: 'utc' }) : null;
  const localSelectedSlotDateTime = utcSelectedSlotDateTime ? utcSelectedSlotDateTime.setZone(timezone) : null;
  const selectedSlotIsToday = !!slots?.find((slot) => slot?.slotId && slot.slotId === selectedId);
  const selectedSlotIsVisible = !!sliceAndFill(slots, slotCount)?.find(
    (slot) => slot?.slotId && slot.slotId === selectedId
  );

  return (
    <StyledSlotDay $hasSlots={slots.length > 0} data-testid={`slot-day-${dayDate.setZone(timezone).toFormat('ccc')}`}>
      {showAll && (
        <Modal
          title={`${clinicianFullName} ${dayDate.setZone(timezone).toFormat('cccc d MMMM yyyy')}`}
          open={showAll}
          onCancel={() => setShowAll(false)}
          footer={null}
          centered
          width={520}
          styles={{ body: { padding: '12px 12px 24px' } }}
          data-testid="more-slots-modal"
          destroyOnHidden
        >
          {clinicianFullName === '' ? (
            <p>
              {t('Select a start time for a ')}
              {(clinician as ClinicianType).slot_duration} {t('minute appointment with')} {clinicianFullName} (
              {(clinician as ClinicianType).client_name || (clinician as F2FConsultantType).service}) on{' '}
              {dayDate.setZone(timezone).toFormat('cccc d MMMM yyyy')}.
            </p>
          ) : (
            <p>
              {t('Select a start time for an appointment on ')}
              {dayDate.setZone(timezone).toFormat('cccc d MMMM yyyy')}.
            </p>
          )}
          <StyledAllSlotsBox>
            {slots.map((slot, i) => {
              const utcSlotDateTime = DateTime.fromJSDate(slot.start, { zone: 'utc' });
              const localSlotDateTime = utcSlotDateTime.setZone(timezone);
              const slotTitle = slotTooltipUtc
                ? DateTime.fromJSDate(slot.start).setZone('utc').toFormat("d MMMM yyyy 'at' HH:mm 'UTC'")
                : localSlotDateTime.toFormat("d MMMM yyyy 'at' HH:mm ZZZZ");

              const Button = slot?.slotId && slot.slotId === selectedId ? StyledSelectedSlotButton : StyledSlotButton;

              return (
                <Button
                  key={slot.start.getTime()}
                  title={slotTitle}
                  aria-label={localSlotDateTime.toFormat('HH:mm, cccc, d MMMM yyyy')}
                  onClick={() => {
                    selectSlot(slot);
                    setShowAll(false);
                  }}
                  autoFocus={i === 0}
                  data-testid={`more-slots-modal-slot-${slot.start.getTime()}`}
                >
                  <span aria-hidden>{localSlotDateTime.toFormat('HH:mm')}</span>
                </Button>
              );
            })}
          </StyledAllSlotsBox>
        </Modal>
      )}
      <div className="slot-date">
        <div>{dayDate.setZone(timezone).toFormat('ccc')}</div>
        <div>{dayDate.setZone(timezone).toFormat('d MMM')}</div>
      </div>

      {slots.length === 0 && <StyledEmptyState image={false} description={t('No slots available')} />}

      {sliceAndFill(slots, slotCount).map((slot, i) => {
        if (!slot?.start) {
          return null;
        }

        const utcSlotDateTime = DateTime.fromJSDate(slot.start, { zone: 'utc' });
        const localSlotDateTime = utcSlotDateTime.setZone(timezone);
        const slotTitle = slotTooltipUtc
          ? DateTime.fromJSDate(slot.start).setZone('utc').toFormat("d MMMM yyyy 'at' HH:mm 'UTC'")
          : localSlotDateTime.toFormat("d MMMM yyyy 'at' HH:mm ZZZZ");

        const Button = slot.slotId && slot.slotId === selectedId ? StyledSelectedSlotButton : StyledSlotButton;

        return (
          <Button
            key={slot.start.getTime()}
            title={slotTitle}
            aria-label={localSlotDateTime.toFormat('HH:mm, cccc, d MMMM yyyy')}
            onClick={() => selectSlot(slot)}
            data-testid={`slot-button-${slot.start.getTime()}`}
          >
            <span aria-hidden>{localSlotDateTime.toFormat('HH:mm')}</span>
          </Button>
        );
      })}

      {slots.length > slotCount && (
        <StyledMoreButton
          type={selectedSlotIsToday && !selectedSlotIsVisible ? 'primary' : 'link'}
          onClick={() => setShowAll(true)}
          aria-label={`More slots on ${dayDate.setZone(timezone).toFormat('cccc, d MMMM yyyy')}`}
          data-testid="more-slots-button"
        >
          <>
            <span aria-hidden>{t('More')}</span>
            {selectedSlotIsToday && !selectedSlotIsVisible && localSelectedSlotDateTime && (
              <div className="more-time">- {localSelectedSlotDateTime.toFormat('HH:mm')}</div>
            )}
          </>
        </StyledMoreButton>
      )}
    </StyledSlotDay>
  );
};

const MOBILE_BREAKPOINT = 992;

export const SlotGrid = ({
  clinician,
  slots,
  timezone,
  weekCount,
  maxWeekCount,
  setWeekCount,
  selectSlot,
  selectedId,
  mobileView,
  startDate
}: {
  clinician: ClinicianType | F2FConsultantType | RandoxSlotType;
  slots: AppointmentSlot[];
  timezone?: string;
  weekCount: number;
  maxWeekCount?: number;
  setWeekCount: Dispatch<SetStateAction<number>>;
  selectSlot: (slot: AppointmentSlot | RandoxSlot) => void;
  mobileView?: boolean;
  selectedId?: string;
  startDate: DateTime | null;
}) => {
  const { t } = useTranslation('common');
  const { width } = useViewportSize();
  const isMobile = mobileView || width < MOBILE_BREAKPOINT;

  const { sliceParams, isFirstChunk, goForward, goBackward } = useWeekChunkNavigation({
    isMobile,
    setWeekCount
  });

  const slotsByDay: { [isoDate: string]: AppointmentSlot[] } | undefined = useMemo(() => {
    const sortedSlots = [...(slots ?? [])].sort((a, b) => +new Date(a.start) - +new Date(b.start));
    const earliestSlotDate = startDate?.startOf('day') || DateTime.fromJSDate(slots?.[0]?.start).setZone(timezone);
    const slotsGroupedByDate = groupBy(sortedSlots, (slot) =>
      DateTime.fromJSDate(slot.start).setZone(timezone).toISODate()
    );
    const weekRange: { [isoDate: string]: AppointmentSlot[] } = Array.from({ length: 7 }).reduce<{
      [isoDate: string]: AppointmentSlot[];
    }>(
      (acc, _, i) => {
        const date = earliestSlotDate.plus({ days: i }).toISODate();
        if (!date) return acc;
        acc[date] = [];
        return acc;
      },
      {} as { [isoDate: string]: AppointmentSlot[] }
    );
    return { ...weekRange, ...slotsGroupedByDate };
  }, [slots, startDate, timezone]);

  const slicedSlots = toPairs(slotsByDay).slice(...sliceParams);

  const isBackDisabled = (weekCount === 0 && isFirstChunk) || slicedSlots.length === 0;
  const isForwardDisabled = (maxWeekCount != null && weekCount >= maxWeekCount) || slicedSlots.length === 0;

  return (
    <StyledSlotBox>
      <div className="columns">
        <StyledWeekButton
          onClick={goBackward}
          disabled={isBackDisabled}
          aria-label={t('Previous week')}
          icon={<ArrowLeftOutlined aria-hidden />}
          data-testid="previous-week-button"
        />
        {slicedSlots.map(([day, slots]) => (
          <Day
            key={day}
            clinician={clinician}
            day={day}
            slots={slots}
            timezone={timezone}
            selectSlot={selectSlot}
            selectedId={selectedId}
            slotCount={5}
          />
        ))}
        <StyledWeekButton
          onClick={goForward}
          disabled={isForwardDisabled}
          aria-label={t('Next week')}
          icon={<ArrowRightOutlined aria-hidden />}
          data-testid="next-week-button"
        />
      </div>
    </StyledSlotBox>
  );
};

const StyledSkeleton = styled.div`
  padding: 10px;
  background: #fff;
  height: 300px;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 10px;

  @keyframes loading {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
    100% {
      opacity: 1;
    }
  }

  > div {
    background: rgba(0, 0, 0, 0.05);
    animation: loading 1.5s infinite;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);

    > div.mobile-dependent {
      display: none;
    }
  }
`;

export const SlotGridSkeleton = () => (
  <StyledSkeleton data-testid="spin-component">
    <div />
    <div />
    <div />
    <div />
    <div className="mobile-dependent" />
    <div className="mobile-dependent" />
    <div className="mobile-dependent" />
  </StyledSkeleton>
);
